import { useState } from 'react';
import { getTournament, getTournamentReferees, getTournamentRegistrations, getBouts, getAgeGroups } from '../../api/client';
import type { Bout, Ring, TournamentReferee, Registration, AgeGroup } from '../../types';
import { enumerateDays } from '../../utils/calendarDate';

interface EditBoutData {
  bout: Bout;
  rings: Ring[];
  referees: TournamentReferee[];
  registrations: Registration[];
  bouts: Bout[];
  tournamentDays: string[];
  ageGroups: AgeGroup[];
  tournamentType: string;
}

/**
 * Loads everything the edit-bout form needs, in one parallel fetch.
 *
 * The caller renders `BoutFormModal` when `editData` is not null. A component
 * wrapper also lived here until #274 removed it — nothing imported it, and it
 * called `openEditBout` from the render body, which React forbids.
 *
 * Usage:
 *   const { editData, openEditBout, closeEditBout } = useEditBout();
 */
export function useEditBout() {
  const [editData, setEditData] = useState<EditBoutData | null>(null);

  async function openEditBout(bout: Bout) {
    if (!bout?.tournament_id) return;
    const [tournament, refs, regs, bouts, ags] = await Promise.all([
      getTournament(bout.tournament_id).then(r => r.data),
      getTournamentReferees(bout.tournament_id).then(r => r.data),
      getTournamentRegistrations(bout.tournament_id).then(r => r.data),
      getBouts(bout.tournament_id).then(r => r.data),
      getAgeGroups().then(r => r.data),
    ]);
    // enumerateDays, not a toISOString() loop: that converts to UTC and yields
    // yesterday after 21:00 at UTC+3, so the bout-day dropdown could offer the
    // wrong days during an evening tournament. #322
    const days = tournament.date_start
      ? enumerateDays(tournament.date_start, tournament.date_end)
      : [];
    setEditData({ bout, rings: tournament.rings || [], referees: refs, registrations: regs, bouts, tournamentDays: days, ageGroups: ags, tournamentType: tournament.type });
  }

  function closeEditBout() { setEditData(null); }

  return { editData, openEditBout, closeEditBout };
}
